// window.WirefaceChat: a function (queue form, for snippets that call before the script loads) with methods.
// The method form talks to the first chat on the page; get(bot) reaches others.
import { ChatInstance } from './instance.ts';
import type { BootOptions, WirefaceChatGlobal, WirefaceChatInstance } from './types.ts';

export const VERSION = typeof __WFC_VERSION__ === 'string' ? __WFC_VERSION__ : '0.0.0';

const METHODS = [
  'open',
  'close',
  'toggle',
  'show',
  'hide',
  'update',
  'sendMessage',
  'prefill',
  'startVoice',
  'stopVoice',
  'identify',
  'setContext',
  'page',
  'registerTool',
  'newConversation',
  'setLocale',
  'setTheme',
  'consent',
  'on',
  'once',
  'off',
  'shutdown',
] as const;

export interface Registry {
  global: WirefaceChatGlobal;
  boot(o: BootOptions, defaultHost: string): Promise<ChatInstance>;
  instances: ChatInstance[];
}

export function createGlobal(defaultHost: string, nonce: string | null): Registry {
  const instances: ChatInstance[] = [];
  /** calls made before any chat was booted wait for the first one */
  let firstResolve: (i: ChatInstance) => void = () => {};
  const first = new Promise<ChatInstance>(r => (firstResolve = r));

  const boot = async (o: BootOptions, host = defaultHost): Promise<ChatInstance> => {
    if (!o?.bot) throw new Error('[Wireface Chat] boot() needs a bot id: WirefaceChat.boot({ bot: "pk_..." })');
    const existing = instances.find(i => i.bot === o.bot && (!o.target || i.layout() === 'inline'));
    if (existing) return existing;
    const instance = new ChatInstance(o, o.host ?? host, {
      others: () => instances,
      nonce,
      removed: i => {
        const idx = instances.indexOf(i);
        if (idx >= 0) instances.splice(idx, 1);
      },
    });
    instances.push(instance);
    if (instances.length === 1) firstResolve(instance);
    return instance;
  };

  const call = async (method: string, ...args: unknown[]): Promise<unknown> => {
    if (method === 'boot') return boot(args[0] as BootOptions);
    const target = instances[0] ?? (await first);
    const fn = (target as unknown as Record<string, (...a: unknown[]) => unknown>)[method];
    if (typeof fn !== 'function') throw new Error(`[Wireface Chat] unknown method ${method}`);
    return fn.apply(target, args);
  };

  const global = ((command: string, ...args: unknown[]) => call(command, ...args)) as WirefaceChatGlobal;
  const g = global as unknown as Record<string, unknown>;
  for (const m of METHODS) {
    g[m] = (...args: unknown[]) => {
      // synchronous ones (on/off/registerTool/show/hide/page/consent) work before a chat exists by waiting for it
      const target = instances[0];
      if (target) return (target as unknown as Record<string, (...a: unknown[]) => unknown>)[m]!(...args);
      if (m === 'on' || m === 'once' || m === 'registerTool') {
        let off: (() => void) | null = null;
        let cancelled = false;
        void first.then(i => {
          if (!cancelled) off = (i as unknown as Record<string, (...a: unknown[]) => () => void>)[m]!(...args);
        });
        return () => {
          cancelled = true;
          off?.();
        };
      }
      return call(m, ...args);
    };
  }
  Object.defineProperties(global, {
    boot: { value: (o: BootOptions) => boot(o) },
    get: {
      value: (bot?: string) =>
        (bot ? instances.find(i => i.bot === bot) : instances[0]) as WirefaceChatInstance | undefined,
    },
    instances: { value: () => [...instances] as WirefaceChatInstance[] },
    version: { value: VERSION },
    bot: { get: () => instances[0]?.bot },
    isOpen: { value: () => instances[0]?.isOpen() ?? false },
    getState: {
      value: () => instances[0]?.getState() ?? { open: false, conversationId: null, visitorId: null, unread: 0 },
    },
    ready: { get: () => (instances[0]?.ready ?? first.then(i => i.ready)) as Promise<void> },
  });
  return { global, boot, instances };
}

/** Follow navigation in single-page apps: one patch of history for all chats on the page. */
export function watchRoutes(onChange: () => void) {
  const fire = () => queueMicrotask(onChange);
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method];
    history[method] = function (this: History, ...args: Parameters<History['pushState']>) {
      const out = original.apply(this, args);
      fire();
      return out;
    };
  }
  window.addEventListener('popstate', fire);
  window.addEventListener('hashchange', fire);
  document.addEventListener('turbo:load', fire);
}
